import React, {useRef} from 'react';
import {
  AccessibilityInfo,
  Pressable,
  ScrollView,
  useWindowDimensions,
  View,
} from 'react-native';
import {noop} from 'lodash';

import Icon from '/component/base/Icon';
import BasePopover from '/component/base/Popover';
import Text from '/component/base/Text';
import {Refresh} from '/constant/TestIDs';
import {withTranslation} from '/provider/TranslationProvider';
import {useAccessibilityFocus} from '/util/hooks/useAccessibilityFocus.hook';

import {styles} from './RefreshInfoPopover.style';
import {getSections} from './RefreshInfoPopover.util';

interface RefreshInfoPopoverProps {
  tt: (key: string, value: string) => string;
  ts: (key: string, value: string) => string;
  isCoach: boolean;
  popoverClose: () => void;
}

export const RefreshInfoPopover: React.FC<RefreshInfoPopoverProps> = ({
  ts = withTranslation.tsDefaultProp,
  isCoach = false,
  popoverClose = noop,
}) => {
  const {height: windowHeight} = useWindowDimensions();
  const fontResizeProps = {
    allowFontScaling: true,
    maxFontSizeMultiplier: 2,
  } as const;
  const maxBodyHeight = Math.max(220, windowHeight * 0.6);

  const sections = getSections({isCoach, ts});
  const announcementCancelled = useRef(false);
  const activeTimeouts = useRef<NodeJS.Timeout[]>([]);
  const closeButtonRef = useRef(null);
  const isScreenReaderEnabled = useAccessibilityFocus(closeButtonRef);

  const handleClose = async () => {
    announcementCancelled.current = true;
    activeTimeouts.current.forEach(timeout => clearTimeout(timeout));
    activeTimeouts.current = [];

    await AccessibilityInfo.announceForAccessibility(
      ts('popoverClosed', 'Popover closed'),
    );
    popoverClose();
  };

  return (
    <BasePopover
      padded
      style={{base: styles.container}}
      accessibilityViewIsModal={true}>
      <View accessible={!isScreenReaderEnabled} style={styles.headerRow}>
        <Pressable
          ref={closeButtonRef}
          style={styles.iconContainer}
          accessible
          accessibilityRole="button"
          accessibilityLabel="Close"
          onPress={handleClose}>
          <Icon name="close" style={styles.closeIcon} />
        </Pressable>
        <Text
          accessible={true}
          accessibilityRole="header"
          accessibilityLabel="Data refresh timetables"
          variant="large"
          style={styles.heading}
          {...fontResizeProps}>
          Data refresh timetables
        </Text>
      </View>
      <ScrollView
        style={[styles.scrollBody, {maxHeight: maxBodyHeight}]}
        contentContainerStyle={styles.scrollContent}
        nestedScrollEnabled
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator>
        {sections.map((section, sectionIndex) => (
          <View
            accessible={!isScreenReaderEnabled}
            key={sectionIndex}
            style={styles.metric}>
            <Icon accessible={false} name={section.icon} style={styles.icon} />
            <View accessible={false} style={styles.body}>
              <Text
                variant="heading"
                style={styles.metricTitle}
                testID={Refresh.metricsTitle}
                accessible={true}
                accessibilityLabel={section.title}
                accessibilityRole="header"
                {...fontResizeProps}>
                {section.title}
              </Text>

              <Text
                testID={Refresh.metricsBody}
                variant="small"
                style={styles.metricSubhead}
                accessible
                accessibilityRole="text"
                {...fontResizeProps}>
                {section.subhead}
              </Text>

              {section.items.map((item, itemIndex) => (
                <Text
                  testID={Refresh.metricsBody}
                  key={itemIndex}
                  style={styles.bulletPoints}
                  accessible
                  accessibilityRole="text"
                  {...fontResizeProps}>
                  {'\u2022 '}
                  {item}
                </Text>
              ))}
            </View>
          </View>
        ))}
      </ScrollView>
    </BasePopover>
  );
};

export default withTranslation('refreshInfoPopover')(RefreshInfoPopover);
